import { useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Textarea } from '@/new/components/shadcn/textarea';

export interface IssueFormItem {
  id: string;
  label: string;
  text: string;
}

interface IssuesListProps {
  issues: IssueFormItem[];
  onChange: (issues: IssueFormItem[]) => void;
}

export function IssuesList({ issues, onChange }: IssuesListProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [addLabel, setAddLabel] = useState('');
  const [addText, setAddText] = useState('');

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editText, setEditText] = useState('');

  const handleAdd = () => {
    const trimmedLabel = addLabel.trim();
    if (!trimmedLabel) return;
    const trimmedText = addText.trim();
    onChange([...issues, { id: uuidv4(), label: trimmedLabel, text: trimmedText }]);
    trackCustomEvent(TrackingEventsV2.MatterContextIssueAdd);
    setAddLabel('');
    setAddText('');
    setShowAddModal(false);
  };

  const openEditModal = (index: number) => {
    const issue = issues[index];
    setEditingIndex(index);
    setEditLabel(issue.label);
    setEditText(issue.text);
  };

  const saveEdit = () => {
    if (editingIndex === null) return;
    const trimmedLabel = editLabel.trim();
    const trimmedText = editText.trim();
    if (trimmedLabel || trimmedText) {
      const updated = [...issues];
      updated[editingIndex] = {
        ...updated[editingIndex],
        label: trimmedLabel,
        text: trimmedText,
      };
      onChange(updated);
      trackCustomEvent(TrackingEventsV2.MatterContextIssueEdit);
    }
    setEditingIndex(null);
  };

  const handleDelete = (index: number) => {
    onChange(issues.filter((_, i) => i !== index));
    trackCustomEvent(TrackingEventsV2.MatterContextIssueDelete);
  };

  return (
    <div>
      <Button size="sm" onClick={() => setShowAddModal(true)}>
        <Icon className="lucide--plus" />
        Add Issue
      </Button>

      {issues.length > 0 && (
        <ul className="mt-4 space-y-2">
          {issues.map((issue, i) => (
            <li key={issue.id ?? `new-${i}`} className="flex items-start gap-3 text-sm">
              <span className="mt-2 flex-shrink-0 text-gray-400 tabular-nums">{i + 1}.</span>

              <div
                className="group flex flex-1 cursor-pointer items-start justify-between rounded-md border bg-gray-50 px-3 py-2 transition-colors hover:border-gray-400"
                onClick={() => openEditModal(i)}
              >
                <div className="flex-1">
                  <span className="font-medium text-gray-800">{issue.label}</span>
                  {issue.text && issue.text !== issue.label && <p className="mt-0.5 text-gray-500">{issue.text}</p>}
                </div>

                <div className="mt-1 ml-3 flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditModal(i);
                    }}
                    className="text-gray-300 hover:text-gray-500"
                  >
                    <Icon className="lucide--pencil text-xs" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(i);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Icon className="lucide--x text-xs" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Add Issue Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Icon className="lucide--plus" />
              Add Issue
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="add-issue-label">Label</Label>
              <Input
                id="add-issue-label"
                onChange={(e) => setAddLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
                placeholder="e.g. Breach of Contract"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-issue-text">
                Description <span className="text-gray-400">(optional)</span>
              </Label>
              <Textarea
                id="add-issue-text"
                value={addText}
                onChange={(e) => setAddText(e.target.value)}
                rows={3}
                placeholder="Additional detail about this issue..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={!addLabel.trim()}>
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Issue Modal */}
      <Dialog open={editingIndex !== null} onOpenChange={(open) => !open && setEditingIndex(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Icon className="lucide--pencil" />
              Edit Issue
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="edit-issue-label">Label</Label>
              <Input id="edit-issue-label" value={editLabel} onChange={(e) => setEditLabel(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-issue-text">Description</Label>
              <Textarea id="edit-issue-text" value={editText} onChange={(e) => setEditText(e.target.value)} rows={4} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingIndex(null)}>
              Cancel
            </Button>
            <Button onClick={saveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
